import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import SignatureCanvas from 'react-signature-canvas';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  pad: {
    border: '1px solid #333',
    borderRadius: '4px',
    backgroundColor: 'white',
  },
  controls: {
    marginTop: '10px',
    display: 'flex',
    gap: '10px',
  },
  button: {
    padding: '8px 12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#007bff',
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
  },
  buttonSecondary: {
    backgroundColor: '#6c757d',
  },
  buttonDisabled: {
    backgroundColor: '#cccccc',
    cursor: 'not-allowed',
  },
  error: {
    color: '#d32f2f',
    fontSize: '14px',
    marginTop: '8px',
  },
  placeholder: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#999',
    pointerEvents: 'none',
    fontSize: '14px',
  },
  canvasContainer: {
    position: 'relative',
    display: 'inline-block',
  },
};

function SignaturePad({ 
  onSave, 
  width = 400, 
  height = 200, 
  penColor = '',
  canvasProps = {} 
}) {
  const sigPadRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [error, setError] = useState('');

  const clear = useCallback(() => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      setIsEmpty(true);
      setError('');
    }
  }, []);

  const save = useCallback(() => {
    try {
      if (!sigPadRef.current) {
        setError('Signature pad not initialized');
        return;
      }

      if (sigPadRef.current.isEmpty()) {
        setError('Please provide a signature first.');
        return;
      }

      // Check if onSave is a function
      if (typeof onSave !== 'function') {
        setError('Save functionality is not available');
        console.error('onSave is not a function:', onSave);
        return;
      }

      // Use toDataURL directly to avoid trim_canvas issues
      const dataUrl = sigPadRef.current.toDataURL('image/png');
      
      if (!dataUrl || dataUrl === 'data:,') {
        setError('Failed to generate signature image');
        return;
      }

      setError('');
      
      // Call onSave with the data URL
      onSave(dataUrl);
      
    } catch (err) {
      console.error('Error saving signature:', err);
      setError('An error occurred while saving the signature');
    }
  }, [onSave]); // Add onSave to dependencies

  const handleBegin = useCallback(() => {
    setError('');
  }, []);

  const handleEnd = useCallback(() => {
    if (sigPadRef.current) {
      setIsEmpty(sigPadRef.current.isEmpty());
    }
  }, []);

  const enhancedCanvasProps = {
    width: width,
    height: height,
    style: {
      ...styles.pad,
      width: `${width}px`,
      height: `${height}px`,
    },
    ...canvasProps,
  };

  return (
    <div style={styles.container}>
      <div style={styles.canvasContainer}>
        <SignatureCanvas
          ref={sigPadRef}
          penColor={penColor}
          minWidth={2}
          maxWidth={3}
          throttle={16}
          onBegin={handleBegin}
          onEnd={handleEnd}
          canvasProps={enhancedCanvasProps}
        />
        
        {isEmpty && (
          <div style={styles.placeholder}>
            Sign here
          </div>
        )}
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      <div style={styles.controls}>
        <button 
          style={{
            ...styles.button,
            ...(isEmpty ? styles.buttonDisabled : {})
          }} 
          onClick={save} 
          aria-label="Save signature"
          disabled={isEmpty}
          type="button"
        >
          Save
        </button>
        <button
          style={{
            ...styles.button, 
            ...styles.buttonSecondary,
            ...(isEmpty ? styles.buttonDisabled : {})
          }}
          onClick={clear}
          aria-label="Clear signature pad"
          disabled={isEmpty}
          type="button"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

// Enhanced PropTypes with isRequired and default props
SignaturePad.propTypes = {
  onSave: PropTypes.func.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  penColor: PropTypes.string,
  canvasProps: PropTypes.object,
};

// Default props for safety
SignaturePad.defaultProps = {
  onSave: (dataUrl) => {
    console.warn('onSave callback not provided. Signature data:', dataUrl);
    // You can provide a default behavior here, like downloading the image
    // const link = document.createElement('a');
    // link.download = 'signature.png';
    // link.href = dataUrl;
    // link.click();
  },
  width: 400,
  height: 200,
  penColor: 'black',
  canvasProps: {},
};

export default SignaturePad;